import React, { useEffect } from "react";
import { Switch, Box, Grid, Backdrop, Button, TextField } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import EditIcon from "@material-ui/icons/Edit";
import { useFormik } from "formik";
import { object, string } from "yup";
import {
  addNewTerminal,
  getTerminal,
  updateTerminal,
} from "src/app-redux/configuration/actions/configurationActions";
import { loader, selectTerminalList } from "src/app-redux/configuration/configurationSlice";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import { IAddApprovalUser, IAddTerminal } from "src/common/typings/configuration";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import { Prompt } from "react-router-dom";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import { Check, Close } from "@material-ui/icons";
import { Tooltip } from "@mui/material";
import { columns } from "./columns";
import { useStyles } from "./styles";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import { terminalTypeList } from "src/common/utils/app.dropdowns";
import CommonButton from "src/components/CommonButton/CommonButton";
import CommonLoader from "src/components/Loader/CommonLoader";

export interface IForm extends IAddApprovalUser {}

interface Props {}

export function InputFields({}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch: any = useAppDispatch();

  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [isEditMode, setisEditMode] = React.useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = React.useState<any>(null);
  const [oldTerminalId, setOldTerminalId] = React.useState<number>(0);

  const loading = useAppSelector(loader);
  const terminalList = useAppSelector(selectTerminalList);

  // Form Submission
  const validationSchema = object({
    terminalName: string()
      .required("This field is required")
      .max(50, "Maximum 50 characters allowed"),
    deserialize: string().max(3000, "Maximum 3000 characters are allowed"),
    alias: string().required("This field is required").max(5, "Maximum 5 characters allowed"),
    terminalType: string().required("This field is required"),
    terminalId: string().required("This field is required"),
  });
  const formik = useFormik<IAddTerminal>({
    enableReinitialize: true,
    initialValues: {
      isActive: false,
      terminalName: "",
      description: "",
      alias: "",
      terminalType: "",
      terminalId: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, actions) => {
      let res;
      if (isEditMode) {
        values.newTerminalId = values.terminalId;
        values.terminalId = oldTerminalId;
        res = await dispatch(updateTerminal(values));
      } else res = await dispatch(addNewTerminal(values));
      if (res?.payload) {
        dispatch(getTerminal());
        actions.setSubmitting(false);
        actions.resetForm();
        setisEditMode(false);
        setIsDirty(false);
        setSelectedRecord(null);
      }
    },
  });
  // End Form Submission
  const handleUpdateClick = (params: any) => {
    setOldTerminalId(params.terminalId);
    setisEditMode(true);
    setSelectedRecord(params);
    formik.setFieldValue("terminalId", params.terminalId);
    formik.setFieldValue("terminalName", params.terminalName);
    formik.setFieldValue("description", params.description);
    formik.setFieldValue("alias", params.alias);
    formik.setFieldValue("isActive", params.isActive);
    formik.setFieldValue("terminalType", params.terminalType);
  };
  useEffect(() => {
    dispatch(getTerminal());
  }, []);

  useEffect(() => {
    if (terminalList.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "actions"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue, row }: any) {
                return (
                  <>
                    <NewCommonButton
                      onClick={() => {
                        setIsDirty(true);
                        handleUpdateClick(row.original);
                      }}
                      icon={<EditIcon />}
                      isEdit={true}
                    >
                      Edit
                    </NewCommonButton>
                  </>
                );
              },
            }
          : column.accessorKey === "description"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue, row }: any) {
                return (
                  <Tooltip title={renderedCellValue} followCursor>
                    <span>{renderedCellValue}</span>
                  </Tooltip>
                );
              },
            }
          : column.accessorKey === "isActive"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue, row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isActive ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isActive ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column.accessorKey === "terminalType"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue, row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      {row.original?.terminalType === 0
                        ? "Border Terminal"
                        : row.original?.terminalType === 1
                        ? "Off Dock"
                        : row.original?.terminalType === 2
                        ? "Dry Port"
                        : row.original?.terminalType === 4
                        ? "Dry Port without Weboc"
                        : row.original?.terminalType === 3
                        ? "HQ Dashboard"
                        : row.original?.terminalType}
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [terminalList]);

  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  useEffect(() => {
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);

  return (
    <>
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <form onSubmit={formik.handleSubmit}>
        <Backdrop className={classes.backdrop} open={loading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          <>
            <Grid className={classes.UserMainPanel}>
              <Grid
                container
                className={classes.mainConainer}
                direction="row"
                alignItems="center"
                spacing={2}
              >
                <Grid container item xs={12} spacing={2}>
                  <Grid item xs={4} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Terminal Name"
                        name="terminalName"
                        placeholder="Enter Terminal Name"
                        disabled={false}
                        value={formik.values.terminalName}
                        onChange={(e) => formik.setFieldValue("terminalName", e.target.value)}
                        error={formik.touched.terminalName && Boolean(formik.errors.terminalName)}
                        helperText={formik.touched.terminalName && formik.errors.terminalName}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={4} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Alias"
                        name="alias"
                        placeholder="Enter Alies"
                        disabled={false}
                        value={formik.values.alias}
                        onChange={(e) => formik.setFieldValue("alias", e.target.value)}
                        error={formik.touched.alias && Boolean(formik.errors.alias)}
                        helperText={formik.touched.alias && formik.errors.alias}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={4} md={4} lg={4} style={{ paddingRight: 0, display: "flex" }}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(formik.values.isActive)}
                            onChange={formik.handleChange}
                            name="isActive"
                            color="primary"
                          />
                        }
                        label="Active"
                      />
                    </Box>
                    <Box mb={1} mt={1} className={"headerButtonsConfig"} style={{ float: "right" }}>
                      <CommonButton
                        // isGreen
                        cssClass="customSubmitBtn"
                        onClick={() => {
                          setisEditMode(false);
                          setIsDirty(false);
                          formik.resetForm();
                          setSelectedRecord(null);
                        }}
                      >
                        Reset
                      </CommonButton>
                      <CommonButton
                        isGreen
                        cssClass="customSubmitBtn"
                        onClick={() => {
                          formik.submitForm();
                        }}
                      >
                        {isEditMode ? "Update" : "Add"}
                      </CommonButton>
                    </Box>
                  </Grid>
                  <Grid item xs={4} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box>
                      <TextField
                        multiline
                        rows={1}
                        rowsMax={5}
                        size="small"
                        fullWidth
                        className="customTextField"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Descirption"
                        name="descirption"
                        id="descirption"
                        placeholder="Enter Descirption"
                        disabled={false}
                        value={formik.values.description}
                        onChange={(e) => formik.setFieldValue("description", e.target.value)}
                        error={formik.touched.description && Boolean(formik.errors.description)}
                        helperText={formik.touched.description && formik.errors.description}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={4} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        disabled={false}
                        label="Terminal Type"
                        placeholder="Select Terminal Type"
                        name="terminalType"
                        optionsType={terminalTypeList}
                        getOptionLabel={(option) => option.name}
                        renderOption={(option) => <span>{option.name}</span>}
                        required
                        value={
                          terminalTypeList.find(
                            (st: any) => st.value === formik.values.terminalType
                          ) || null
                        }
                        onChange={(option: any) => {
                          option && option !== ""
                            ? formik.setFieldValue("terminalType", option.value)
                            : "";
                        }}
                        error={formik.touched.terminalType && Boolean(formik.errors.terminalType)}
                        helperText={formik.touched.terminalType && formik.errors.terminalType}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={4} md={4} lg={4} style={{ paddingRight: 0 }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        required
                        type="number"
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Terminal Id"
                        name="terminalId"
                        placeholder="Enter Terminal Id"
                        disabled={false}
                        value={formik.values.terminalId}
                        onChange={(e) => formik.setFieldValue("terminalId", e.target.value)}
                        error={formik.touched.terminalId && Boolean(formik.errors.terminalId)}
                        helperText={formik.touched.terminalId && formik.errors.terminalId}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <CommonMaterialGrid
                      defaultPerPageRecord={10}
                      height={400}
                      rows={terminalList || []}
                      columns={displayColumns}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </>
        </Grid>
      </form>
    </>
  );
}
